var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import AppConfig, { ALGOLIA } from '../../config/config';
const defaultFacets = [
    'vendor',
    'product_type',
    'meta.custom.metafield_product_colour',
    'price',
];
// Generate or retrieve user token for personalization
function getUserToken() {
    if (typeof window === 'undefined')
        return 'server-side-token';
    // Check if user token exists in localStorage
    let userToken = localStorage.getItem('algolia_user_token');
    if (!userToken) {
        // Generate a new user token
        userToken = `user-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
        localStorage.setItem('algolia_user_token', userToken);
    }
    return userToken;
}
// Retry logic for API calls
function fetchWithRetry(url_1, options_1) {
    return __awaiter(this, arguments, void 0, function* (url, options, retries = 3) {
        for (let i = 0; i < retries; i++) {
            try {
                const response = yield fetch(url, options);
                if (response.status === 429) {
                    // Rate limited - wait and retry
                    const retryAfter = response.headers.get('Retry-After') || '1';
                    const waitTime = parseInt(retryAfter) * 1000;
                    console.warn(`Algolia rate limit hit, waiting ${waitTime}ms before retry`);
                    yield new Promise(resolve => setTimeout(resolve, waitTime));
                    continue;
                }
                return response;
            }
            catch (error) {
                if (i === retries - 1)
                    throw error;
                // Wait before retrying on network errors
                yield new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
            }
        }
        throw new Error('Max retries reached');
    });
}
function paramsToQueryString(params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
                searchParams.set(key, JSON.stringify(value));
            }
            else {
                searchParams.set(key, String(value));
            }
        }
    });
    return searchParams.toString();
}
export function fetchAlgolia(r) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        const { appId, apiKey, indexName } = ALGOLIA;
        if (!appId || !apiKey || !indexName) {
            throw new Error('Algolia configuration is missing. Please check your environment variables.');
        }
        let targetIndexName = indexName;
        if (r.sort && r.sort !== 'relevance') {
            const sortMap = {
                title_asc: `${indexName}_title_asc`,
                title_desc: `${indexName}_title_desc`,
                price_asc: `${indexName}_price_asc`,
                price_desc: `${indexName}_price_desc`,
            };
            targetIndexName = sortMap[r.sort] || indexName;
        }
        const filters = [];
        const facetFilters = [];
        // Process user filters
        if (r.filters) {
            Object.entries(r.filters).forEach(([filterId, values]) => {
                if (values.length > 0) {
                    if (filterId === 'price') {
                        const lastValue = values[values.length - 1];
                        const [min, max] = lastValue.split(',').map(Number);
                        filters.push(`price:${min} TO ${max}`);
                    }
                    else {
                        const filterGroup = values.length === 1
                            ? `${filterId}:${values[0]}`
                            : values.map((value) => `${filterId}:${value}`);
                        facetFilters.push(filterGroup);
                    }
                }
            });
        }
        const baseParams = {
            query: r.q || '',
            maxValuesPerFacet: 100,
            distinct: true,
            facetingAfterDistinct: true,
            userToken: ((_b = (_a = r.merchandising) === null || _a === void 0 ? void 0 : _a.userProfile) === null || _b === void 0 ? void 0 : _b.userToken) || getUserToken(),
            enablePersonalization: ((_c = r.merchandising) === null || _c === void 0 ? void 0 : _c.enablePersonalization) !== false,
        };
        // Get page type and relevant variable from URL
        let collectionFilter = '';
        let collectionName = '';
        let searchQuery = '';
        let isSearchPage = false;
        let isCollectionPage = false;
        if (typeof window !== 'undefined') {
            const { pathname, search } = window.location;
            if (pathname.startsWith('/search')) {
                isSearchPage = true;
                // Extract q param from search string
                const params = new URLSearchParams(search);
                searchQuery = params.get('q') || '';
            }
            else if (pathname.startsWith('/collections/')) {
                isCollectionPage = true;
                const parts = pathname.split('/');
                const collectionIndex = parts.indexOf('collections');
                if (collectionIndex !== -1 && parts.length > collectionIndex + 1) {
                    collectionName = parts[collectionIndex + 1];
                    collectionFilter = `collections:"${collectionName}"`;
                }
            }
        }
        // If no collection detected and not search, default to 'men'
        if (!isSearchPage && !collectionName) {
            collectionName = 'men';
            collectionFilter = `collections:"men"`;
        }
        // If search page, you can use searchQuery as needed
        // If collection page, you can use collectionName as needed
        // Get current collection ID
        const currentCollectionId = (_d = window.collectionData) === null || _d === void 0 ? void 0 : _d.id;
        if (!currentCollectionId && !isSearchPage) {
            console.warn('Collection ID is not available in window.collectionData');
        }
        let allFacets = [];
        let ruleContexts = [];
        if (isSearchPage) {
            // For search pages, use all default facets to get comprehensive results
            allFacets = [...defaultFacets];
            ruleContexts = ((_e = r.merchandising) === null || _e === void 0 ? void 0 : _e.ruleContexts) || []; // Allow merchandising rule contexts
        }
        else {
            // For collection pages, use the existing collection facet logic
            const algoliaShopify = window.algoliaShopify;
            console.log('algoliaShopify object:', algoliaShopify);
            console.log('currentCollectionId:', currentCollectionId);
            const facetsConfig = (_f = algoliaShopify === null || algoliaShopify === void 0 ? void 0 : algoliaShopify.config) === null || _f === void 0 ? void 0 : _f.collection_facets;
            console.log('facetsConfig:', facetsConfig);
            const key = facetsConfig &&
                typeof currentCollectionId === 'string' &&
                currentCollectionId in facetsConfig
                ? currentCollectionId
                : 'default';
            console.log('Using facets key:', key);
            const collectionFacets = ((_h = (_g = facetsConfig === null || facetsConfig === void 0 ? void 0 : facetsConfig[key]) === null || _g === void 0 ? void 0 : _g.filter((facet) => facet.enabled)) === null || _h === void 0 ? void 0 : _h.map((facet) => facet.name)) || [];
            console.log('collectionFacets from config:', collectionFacets);
            // Fallback to default facets if no collection facets found
            const defaultCollectionFacets = collectionFacets.length > 0 ? collectionFacets : defaultFacets;
            // Use all available facets in a single request
            allFacets = [
                ...(collectionFacets.length > 0
                    ? collectionFacets
                    : defaultCollectionFacets),
            ];
            console.log('Final allFacets:', allFacets);
            // Combine collection context with merchandising contexts
            ruleContexts = [collectionName, ...(((_j = r.merchandising) === null || _j === void 0 ? void 0 : _j.ruleContexts) || [])];
        }
        const mainSearchParams = Object.assign(Object.assign({}, baseParams), { page: (r.page || 1) - 1, hitsPerPage: r.pageSize || AppConfig.page.defaultSize, facets: allFacets, analytics: true, ruleContexts: ruleContexts, clickAnalytics: true, enableABTest: ((_k = r.merchandising) === null || _k === void 0 ? void 0 : _k.abTest) ? true : false, enableRules: ((_l = r.merchandising) === null || _l === void 0 ? void 0 : _l.enableRules) !== false });
        // Add A/B test parameters if provided
        if ((_m = r.merchandising) === null || _m === void 0 ? void 0 : _m.abTest) {
            if (r.merchandising.abTest.testId) {
                mainSearchParams.abTest = r.merchandising.abTest.testId;
            }
            if (r.merchandising.abTest.variantId !== undefined) {
                mainSearchParams.variant = r.merchandising.abTest.variantId;
            }
        }
        // Add merchandising filters if provided (these override regular filters)
        if ((_o = r.merchandising) === null || _o === void 0 ? void 0 : _o.merchandisingFilters) {
            mainSearchParams.filters = r.merchandising.merchandisingFilters;
        }
        // Set filters based on page type
        if (isSearchPage) {
            // For search pages, only apply user filters (no collection filter)
            if (filters.length > 0) {
                mainSearchParams.filters = filters.join(' AND ');
            }
        }
        else {
            // For collection pages, apply collection filter + user filters
            if (filters.length > 0) {
                mainSearchParams.filters = `${collectionFilter} AND ${filters.join(' AND ')}`;
            }
            else {
                mainSearchParams.filters = collectionFilter;
            }
        }
        if (facetFilters.length > 0) {
            mainSearchParams.facetFilters = facetFilters;
        }
        const searchRequests = [
            {
                indexName: targetIndexName,
                params: paramsToQueryString(mainSearchParams),
            },
        ];
        // Find which facets are filtered
        const filteredFacetIds = r.filters
            ? Object.keys(r.filters).filter((facetId) => {
                var _a;
                return Array.isArray((_a = r.filters) === null || _a === void 0 ? void 0 : _a[facetId]) &&
                    r.filters[facetId].length > 0;
            })
            : [];
        // For each filtered facet, add a request with only that facet's filter - this way we can show facets that are not included in the main response. Eg: Filtering blue swatch, will still show all other swatches. Whereas if we only used the main request, it would not show any other swatches.
        filteredFacetIds.forEach((facetId) => {
            const otherFacetFilters = [];
            if (r.filters) {
                Object.entries(r.filters).forEach(([otherFacetId, otherValues]) => {
                    if (otherFacetId !== facetId &&
                        (otherFacetId !== 'price' || facetId === 'price') &&
                        Array.isArray(otherValues) &&
                        otherValues.length > 0) {
                        if (otherFacetId === 'price') {
                            // Skip price filters when getting facets for price itself
                            // This ensures we get the full price range from the collection
                            return;
                        }
                        else {
                            const filterGroup = otherValues.length === 1
                                ? `${otherFacetId}:${otherValues[0]}`
                                : otherValues.map((value) => `${otherFacetId}:${value}`);
                            otherFacetFilters.push(filterGroup);
                        }
                    }
                });
            }
            const facetRequestParams = Object.assign(Object.assign({}, baseParams), { page: 0, hitsPerPage: 0, facets: facetId === 'price' ? ['price'] : [facetId], analytics: false, clickAnalytics: false, ruleContexts: ruleContexts, enableABTest: false });
            // Set filters based on page type
            if (isSearchPage) {
                // For search pages, don't apply collection filter
                if (otherFacetFilters.length > 0) {
                    facetRequestParams.facetFilters = otherFacetFilters;
                }
            }
            else {
                // For collection pages, apply collection filter
                facetRequestParams.filters = collectionFilter;
                if (otherFacetFilters.length > 0) {
                    facetRequestParams.facetFilters = otherFacetFilters;
                }
            }
            searchRequests.push({
                indexName: targetIndexName,
                params: paramsToQueryString(facetRequestParams),
            });
        });
        try {
            const url = AppConfig.searchSettings.algolia.apiUrl;
            const response = yield fetchWithRetry(url, {
                method: 'POST',
                headers: {
                    'X-Algolia-API-Key': apiKey,
                    'X-Algolia-Application-Id': appId,
                    'Content-Type': 'application/json',
                    'X-Algolia-User-Token': getUserToken(),
                },
                body: JSON.stringify({ requests: searchRequests }),
            });
            if (!response.ok) {
                const errorText = yield response.text();
                console.error('Algolia API error response:', errorText);
                throw new Error(`Algolia API error: ${response.status} ${response.statusText}. Details: ${errorText}`);
            }
            const data = yield response.json();
            const mainResponse = data.results[0];
            const transformedFacetsStats = mainResponse.facets_stats
                ? Object.fromEntries(Object.entries(mainResponse.facets_stats).map(([key, stats]) => {
                    var _a, _b, _c, _d;
                    return [
                        key,
                        {
                            min: (_a = stats.min) !== null && _a !== void 0 ? _a : 0,
                            max: (_b = stats.max) !== null && _b !== void 0 ? _b : 0,
                            avg: (_c = stats.avg) !== null && _c !== void 0 ? _c : 0,
                            sum: (_d = stats.sum) !== null && _d !== void 0 ? _d : 0,
                        },
                    ];
                }))
                : {};
            // Always include all collectionFacets, even if empty
            const facetsWithProducts = {};
            if (mainResponse.facets) {
                allFacets.forEach((facetName) => {
                    if (mainResponse.facets[facetName] &&
                        Object.keys(mainResponse.facets[facetName]).length > 0) {
                        facetsWithProducts[facetName] = mainResponse.facets[facetName];
                    }
                    else {
                        facetsWithProducts[facetName] = {};
                    }
                });
            }
            else {
                allFacets.forEach((facetName) => {
                    facetsWithProducts[facetName] = {};
                });
            }
            // Store the facets config in the response for use in mapper
            const algoliaShopify = window.algoliaShopify;
            const facetsConfig = (_p = algoliaShopify === null || algoliaShopify === void 0 ? void 0 : algoliaShopify.config) === null || _p === void 0 ? void 0 : _p.collection_facets;
            const configKey = facetsConfig && typeof currentCollectionId === 'string' && currentCollectionId in facetsConfig
                ? currentCollectionId
                : 'default';
            const facetConfigForCollection = (facetsConfig === null || facetsConfig === void 0 ? void 0 : facetsConfig[configKey]) || [];
            // --- Merge facet data from extra requests ---
            // For each filtered facet, replace the facet data with the one from the extra request
            filteredFacetIds.forEach((facetId, i) => {
                var _a, _b, _c, _d;
                const extraResponse = data.results[i + 1]; // +1 because main is at 0
                if (extraResponse &&
                    extraResponse.facets &&
                    extraResponse.facets[facetId]) {
                    facetsWithProducts[facetId] = extraResponse.facets[facetId];
                }
                // For price facet, also merge the facets_stats to get the full price range
                if (facetId === 'price' && extraResponse && extraResponse.facets_stats && extraResponse.facets_stats.price) {
                    transformedFacetsStats.price = {
                        min: (_a = extraResponse.facets_stats.price.min) !== null && _a !== void 0 ? _a : 0,
                        max: (_b = extraResponse.facets_stats.price.max) !== null && _b !== void 0 ? _b : 0,
                        avg: (_c = extraResponse.facets_stats.price.avg) !== null && _c !== void 0 ? _c : 0,
                        sum: (_d = extraResponse.facets_stats.price.sum) !== null && _d !== void 0 ? _d : 0,
                    };
                }
            });
            const res = {
                hits: mainResponse.hits || [],
                nbHits: mainResponse.nbHits || 0,
                page: mainResponse.page || 0,
                nbPages: mainResponse.nbPages || 0,
                hitsPerPage: mainResponse.hitsPerPage || 24,
                exhaustiveNbHits: mainResponse.exhaustiveNbHits || true,
                processingTimeMS: mainResponse.processingTimeMS || 0,
                facets: facetsWithProducts,
                facets_stats: transformedFacetsStats,
                query: mainResponse.query || '',
                params: mainResponse.params || '',
                index: mainResponse.index || targetIndexName,
                indexUsed: mainResponse.indexUsed || targetIndexName,
                facetConfig: facetConfigForCollection, // Add the facet config for the mapper
            };
            return res;
        }
        catch (error) {
            console.error('Algolia API error:', error);
            // Provide more specific error messages
            if (error instanceof Error) {
                if (error.message.includes('rate limit')) {
                    throw new Error('Algolia rate limit exceeded. Please try again later.');
                }
                else if (error.message.includes('network')) {
                    throw new Error('Network error connecting to Algolia. Please check your connection.');
                }
            }
            throw new Error(`Algolia API error: ${error}`);
        }
    });
}
/**
 * Track an Algolia Insights event
 */
export function trackAlgoliaEvent(eventType, eventName, data) {
    return __awaiter(this, void 0, void 0, function* () {
        const { appId, apiKey } = ALGOLIA;
        if (!appId || !apiKey) {
            console.warn('Algolia configuration missing for insights tracking');
            return;
        }
        const event = {
            eventType,
            eventName,
            index: data.index,
            userToken: getUserToken(),
            timestamp: Date.now(),
            queryID: data.queryID,
            objectIDs: data.objectIDs,
            positions: data.positions,
        };
        try {
            const response = yield fetch('https://insights.algolia.io/1/events', {
                method: 'POST',
                headers: {
                    'X-Algolia-API-Key': apiKey,
                    'X-Algolia-Application-Id': appId,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    events: [event],
                }),
            });
            if (!response.ok) {
                console.error('Failed to track Algolia event:', response.statusText);
            }
        }
        catch (error) {
            console.error('Error tracking Algolia event:', error);
        }
    });
}
/**
 * Track product click event
 */
export function trackProductClick(productId, position, queryID, index = ALGOLIA.indexName) {
    trackAlgoliaEvent('click', 'Product Clicked', {
        index,
        queryID,
        objectIDs: [productId],
        positions: [position],
    });
}
/**
 * Track product view event
 */
export function trackProductView(productIds, queryID, index = ALGOLIA.indexName) {
    trackAlgoliaEvent('view', 'Products Viewed', {
        index,
        queryID,
        objectIDs: productIds,
    });
}
/**
 * Track conversion event (e.g., add to cart)
 */
export function trackConversion(productId, queryID, index = ALGOLIA.indexName) {
    trackAlgoliaEvent('conversion', 'Product Added to Cart', {
        index,
        queryID,
        objectIDs: [productId],
    });
}
/**
 * Fetch query suggestions from Algolia
 */
export function fetchAlgoliaSuggestions(query_1) {
    return __awaiter(this, arguments, void 0, function* (query, maxSuggestions = 5) {
        const { appId, apiKey } = ALGOLIA;
        const suggestionsIndex = ALGOLIA.suggestionsIndex || `${ALGOLIA.indexName}_query_suggestions`;
        if (!appId || !apiKey || !suggestionsIndex) {
            console.warn('Algolia configuration missing for query suggestions');
            return [];
        }
        if (!query || query.length < 2) {
            return [];
        }
        try {
            const url = `https://${appId}-dsn.algolia.net/1/indexes/${suggestionsIndex}/query`;
            const response = yield fetch(url, {
                method: 'POST',
                headers: {
                    'X-Algolia-API-Key': apiKey,
                    'X-Algolia-Application-Id': appId,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query,
                    hitsPerPage: maxSuggestions,
                    highlightPreTag: '<mark>',
                    highlightPostTag: '</mark>',
                }),
            });
            if (!response.ok) {
                console.error('Failed to fetch Algolia suggestions:', response.statusText);
                return [];
            }
            const data = yield response.json();
            return data.hits.map(hit => ({
                query: hit.query,
                popularity: hit.popularity,
                nb_words: hit.nb_words,
            }));
        }
        catch (error) {
            console.error('Error fetching Algolia suggestions:', error);
            return [];
        }
    });
}
/**
 * Debounce function for search suggestions
 */
export function debounce(func, wait) {
    let timeout = null;
    return function executedFunction(...args) {
        const later = () => {
            timeout = null;
            func(...args);
        };
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(later, wait);
    };
}
/**
 * Create a debounced suggestion fetcher
 */
export const debouncedFetchSuggestions = debounce((query, callback) => __awaiter(void 0, void 0, void 0, function* () {
    const suggestions = yield fetchAlgoliaSuggestions(query);
    callback(suggestions);
}), 300);
